// OnboardingStep2.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import { useAccessibility, useI18N } from "common/hooks";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";

interface OnboardingStep2Props {
  title: string;
  subtitle: string;
}

const OnboardingStep2 = ({ title, subtitle }: OnboardingStep2Props) => {
  const i18n = useI18N();
  const isDark = useDarkMode();

  //set index for swipable views
  const { setScreenIndex } = useAuthModals();

  //fetch data options to display
  const { data } = useAccessibility();

  //get and store selected options for this view
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  // text input option for this step
  const [other, setOther] = useState("");

  // to be firect when submitData?.ability && data is changed
  useEffect(() => {
    if (submitData?.ability && data) {
      const selectedNames = submitData.ability.split(",");
      //find option from data that matches the dataToSubmit name
      const options = data.filter((option) =>
        selectedNames.includes(option.name)
      );

      setSelectedOptions([...options.map((e) => e.name)]);

      //find an option that is in dataToSubmit but not in data
      const otherOption = selectedNames.find(
        (option) => !data.find((o: any) => o.name === option)
      );
      setOther(otherOption || "");
    }
  }, [data, submitData]);

  useEffect(() => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountViewedApplyAccessibilityRequirementPage"
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountViewedApplyAccessibilityRequirementPage",
      {}
    );
  }, []);

  const onBack = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep2BackButtonClicked"
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountStep2BackButtonClicked",
      {}
    );
    setScreenIndex(AuthModalScreen.OnboardingStep1);
  };

  const handleOptionChange = (option: string) => {
    // console.log("option", option);
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((o) => o !== option));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  const onSkip = async () => {
    AnalyticsService.trackGAEventHandler("authPopupCreateAccountStep2Skip");
    AnalyticsService.trackMx("authPopupCreateAccountStep2Skip", {});

    //move to Find the places that work for you
    setScreenIndex(AuthModalScreen.OnboardingStep3);
  };

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler("authPopupCreateAccountStep2Complete");
    AnalyticsService.trackMx("authPopupCreateAccountStep2Complete", {});

    //convert selectionOptions to a comma separated string
    let selectedOptionsString = selectedOptions.map((e) => e).join(",");
    if (other.length > 0) {
      if (selectedOptionsString.length > 0) {
        selectedOptionsString = selectedOptionsString.concat(",", other);
      } else {
        selectedOptionsString = other;
      }
    }

    // console.log("selectedOptionsString", selectedOptionsString);

    setSubmitData((prev) => ({
      ...prev,
      ability: selectedOptionsString,
    }));

    //move to next step
    setScreenIndex(AuthModalScreen.OnboardingStep3);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={{
        height:
          window.innerHeight < 800
            ? `${window.innerHeight - 40}px`
            : `${window.innerHeight - 100}px`,
      }}
      className="flex flex-col rounded-xl bg-white dark:bg-primary-900 overflow-y-auto"
    >
      <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
        <h1
          className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          tabIndex={0}
          ref={firstFocusRef} // Attach the ref here
        >
          {i18n.get("profileSetup")}
        </h1>
      </header>
      <div className="pt-6 px-8 md:px-16">
        <h2
          className="mb-4 text-primary-900 dark:text-primary-100 desktop-h1-xsmall-extra-bold"
          tabIndex={0}
        >
          {title}
        </h2>
        <p
          className="paragraph-medium-medium text-primary-900 dark:text-primary-100"
          tabIndex={0}
        >
          {subtitle}
        </p>
        <div>
          <hr className="w-full border-primary-150 dark:border-primary-200 my-3" />
        </div>
      </div>
      <div className="overflow-y-scroll px-8 md:px-16">
        <div>
          <p
            className="desktop-h2-medium-semi-bold text-primary-900 dark:text-primary-100"
            tabIndex={0}
          >
            {i18n.get("doYouOrSomeoneYouKnowExperienceOrUseAnyOfTheFollowing")}
          </p>
          <p
            className="my-4 paragraph-small-semibold !text-primary-500 dark:!text-primary-300"
            tabIndex={0}
          >
            {i18n.get("selectAllApply")}
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            {data?.map((item, index) => {
              return (
                <div
                  key={index}
                  onClick={() => handleOptionChange(item.name)}
                  role="button"
                  className={`flex items-center gap-1 px-4 py-2.5 rounded-3xl  ${selectedOptions.includes(item.name) ? "bg-orange-300" : "bg-violet-100"} `}
                  tabIndex={0}
                >
                  <p className="text-primary-800 button-small-medium">
                    {item.name}
                  </p>
                </div>
              );
            })}
            <div className="w-full pb-4">
              <FloatingLabelInput
                label="Add your own"
                type="text"
                value={other}
                placeholder="Type here to add your own"
                onChange={(event) => {
                  setOther(event.target.value);
                }}
                onClear={() => {
                  setOther("");
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mb-8 items-center justify-center flex flex-col px-8 md:px-16 pt-2">
        <WideButton
          type="filled"
          onClick={() => onContinue()}
          disabled={selectedOptions.length <= 0 && other.length <= 0}
        >
          {i18n.get("continue")}
        </WideButton>
        <button
          onClick={onSkip}
          className="button-small-semi-bold underline text-links dark:text-white mt-6"
        >
          {i18n.get("skipForNow")}
        </button>
      </div>
      <button onClick={onBack} className="absolute top-4 left-4">
        <ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />
      </button>
    </div>
  );
};

export default OnboardingStep2;
